import { gql, useLazyQuery, useMutation, useSubscription } from '@apollo/client';
import { useAuthenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import { Auth } from 'aws-amplify';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LOGIN_PATH, ROOT_PATH } from 'src/common/constants';
import { ErrorMessage } from 'src/enum/messsage-enum';
import { deleteMstUser, insertMstUser, updateMstUser } from 'src/graphql/mutations';
import { getLogMaintenance, getMstUser } from 'src/graphql/queries';
import { insertedLogMaintenance } from 'src/graphql/subscriptions';
import Maintenance from '../maintenance';

type LocationProps = {
  state: { muMail: string };
};

type MstUser = {
  mu_mail: string;
  mu_user_id: string;
  mu_user_nm: string;
  mu_office_cd: string;
  mu_admin_kb: number;
};

const EntrySample = () => {
  const { user } = useAuthenticator((context) => [context.user]);
  const [isNewData, setIsNewData] = useState(true);
  const [mstUser, setMstUser] = useState<MstUser>({
    mu_mail: '',
    mu_user_id: '',
    mu_user_nm: '',
    mu_office_cd: '',
    mu_admin_kb: 0,
  });

  const location = useLocation() as unknown as LocationProps;
  const navigate = useNavigate();
  const { signOut } = useAuthenticator((context) => [context.user]);

  const [searchMstUser, { data: mstUserData, error: mstUserError }] = useLazyQuery(gql(getMstUser));

  const [insertFunc] = useMutation(gql(insertMstUser), {
    onCompleted: (data) => {
      window.alert('Insert success');
      console.log(data.insertMstUser);
      navigate(ROOT_PATH, { replace: true });
    },
    onError: (error) => {
      // Timeoutの場合はaurora側の起動時間Issueととらえ、リトライする
      if (error?.message === ErrorMessage.GRAPH_QL_TIME_OUT) {
        insertFunc({ variables: { input: mstUser } });
      } else {
        window.alert('Insert failure');
      }
    },
  });
  const [updateFunc] = useMutation(gql(updateMstUser), {
    refetchQueries: [{ query: gql(getMstUser), variables: { input: { mu_mail: mstUser.mu_mail } } }, 'GetMstUser'],
    onCompleted: (data) => {
      window.alert('Update success');
      console.log(data.updateMstUser);
    },
    onError: (error) => {
      // Timeoutの場合はaurora側の起動時間Issueととらえ、リトライする
      if (error?.message === ErrorMessage.GRAPH_QL_TIME_OUT) {
        updateFunc({ variables: { input: mstUser } });
      } else {
        window.alert('Update failure');
      }
    },
  });
  const [deleteFunc] = useMutation(gql(deleteMstUser), {
    onCompleted: (data) => {
      window.alert('Delete success');
      console.log(data.deleteMstUser);
      navigate(ROOT_PATH, { replace: true });
    },
    onError: (error) => {
      // Timeoutの場合はaurora側の起動時間Issueととらえ、リトライする
      if (error?.message === ErrorMessage.GRAPH_QL_TIME_OUT) {
        deleteFunc({ variables: { input: { mu_mail: mstUser.mu_mail } } });
      } else {
        window.alert('Delete failure');
      }
    },
  });

  // リアルタイムのlog_maintenance監視処理
  // 新しくページをロードするUserに対する処理
  const [searchLogMaintenance, { data: logMData, error: logMError }] = useLazyQuery(gql(getLogMaintenance), {
    nextFetchPolicy: 'network-only',
    onError: (error) => {
      // Timeoutの場合はaurora側の起動時間Issueととらえ、リトライする
      if (error?.message === ErrorMessage.GRAPH_QL_TIME_OUT) {
        searchLogMaintenance();
      } else {
        window.alert('Searching log_maintenance error');
      }
    },
  });

  // 操作中のUserに対する処理
  const { data: subInsLogData, error: subInsLogError } = useSubscription(gql(insertedLogMaintenance));

  // Store values to state when page transition
  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      if (location.state?.muMail) {
        searchMstUser({ variables: { input: { mu_mail: location.state?.muMail } } });
        setIsNewData(false);
      }
    }
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (mstUserData)
      setMstUser({
        mu_mail: mstUserData.getMstUser[0].mu_mail,
        mu_user_id: mstUserData.getMstUser[0].mu_user_id,
        mu_user_nm: mstUserData.getMstUser[0].mu_user_nm,
        mu_office_cd: mstUserData.getMstUser[0].mu_office_cd,
        mu_admin_kb: mstUserData.getMstUser[0].mu_admin_kb,
      });
  }, [mstUserData]);

  useEffect(() => {
    searchLogMaintenance();
  }, []);

  // GraphQL Clientでエラーが発生した場合の処理
  if (mstUserError && mstUserError.message !== ErrorMessage.GRAPH_QL_TIME_OUT)
    return <div>{`Error! ${mstUserError.message}`}</div>;
  if (logMError) <div>{`Error! ${logMError.message}`}</div>;
  if (subInsLogError) <div>{`Error! ${subInsLogError.message}`}</div>;

  const handleRegister = async () => {
    authCheck();
    if (isNewData) {
      insertFunc({ variables: { input: mstUser } });
    } else {
      updateFunc({ variables: { input: mstUser } });
    }
  };

  const handleDelete = async () => {
    authCheck();
    deleteFunc({ variables: { input: { mu_mail: mstUser.mu_mail } } });
  };

  const handleCkeckboxClick = () => {
    if (mstUser.mu_admin_kb) {
      setMstUser({ ...mstUser, ...{ mu_admin_kb: 0 } });
    } else {
      setMstUser({ ...mstUser, ...{ mu_admin_kb: 1 } });
    }
  };

  const authCheck = () =>
    Auth.currentAuthenticatedUser().catch(() => {
      signOut();
      navigate(LOGIN_PATH, { replace: true, state: { from: location } });
    });

  if (logMData?.getLogMaintenance.length > 0 || subInsLogData) return <Maintenance />;

  return (
    <div className="flex justify-center">
      <div className="flex flex-col">
        <div>Login User : {user?.username}</div>
        <div className="flex mt-4">
          <span className="flex-grow" />
          <button className="px-2">
            <Link to="/">戻る</Link>
          </button>
        </div>
        <div className="mt-4">
          <div className="flex my-1">
            <label className="w-16">E-Mail</label>
            <input
              className="border border-black"
              value={mstUser.mu_mail}
              readOnly={!isNewData}
              onChange={(e) => setMstUser({ ...mstUser, ...{ mu_mail: e.target.value } })}
            />
          </div>
          <div className="flex my-1">
            <label className="w-16">User ID</label>
            <input
              className="border border-black"
              value={mstUser.mu_user_id}
              onChange={(e) => setMstUser({ ...mstUser, ...{ mu_user_id: e.target.value } })}
            />
          </div>
          <div className="flex my-1">
            <label className="w-16">User Name</label>
            <input
              className="border border-black"
              value={mstUser.mu_user_nm}
              onChange={(e) => setMstUser({ ...mstUser, ...{ mu_user_nm: e.target.value } })}
            />
          </div>
          <div className="flex my-1">
            <label className="w-16">Office Code</label>
            <input
              className="border border-black"
              value={mstUser.mu_office_cd}
              onChange={(e) => setMstUser({ ...mstUser, ...{ mu_office_cd: e.target.value } })}
            />
          </div>
          <div className="flex my-1">
            <label className="w-16">Admin</label>
            <input
              className="border border-black"
              type="checkbox"
              checked={Boolean(mstUser.mu_admin_kb)}
              onChange={handleCkeckboxClick}
            />
          </div>
        </div>
        <div className="flex mt-2 gap-x-1">
          <button className="border border-black bg-gray-300 w-20" onClick={handleRegister}>
            登録
          </button>
          <button className="border border-black bg-gray-300 w-20" onClick={handleDelete} disabled={isNewData}>
            削除
          </button>
        </div>
      </div>
    </div>
  );
};

export default EntrySample;
